import * as React from 'react'
import Image from 'next/image'
import { Button } from '@repo/design-system-cf/components/button'
import {
  IconAddPhotoAlternate,
  IconArrowBack,
  IconFileUpload,
  IconPhotoPrints,
  IconClose,
} from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'
import type { UploadPreview } from '../project-draft-context'
import { useProjectDraft } from '../project-draft-context'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { Typography } from '@repo/design-system-cf/components/typography'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { useVideoUpload } from '../../../_hooks/use-video-upload'
import { useFeatureImageUpload } from '../../../_hooks/use-feature-image-upload'
import { convertImageToWebp } from '../../../utils/image-convertion'

const MAX_IMAGE_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const MAX_VIDEO_FILE_SIZE = 2 * 1024 * 1024 * 1024 // 2GB

export function AddMediaSection() {
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const isInitialOpen = React.useRef(true)

  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')

  const triggerUpload = () => fileInputRef.current?.click()

  const { uploadFeaturedImages } = useFeatureImageUpload()
  const { uploadVideo } = useVideoUpload()

  const [isDragging, setIsDragging] = React.useState(false)
  const [, setFileError] = React.useState<string | null>(null)

  const { steps, setSteps, activeInstructionMedia, setActiveInstructionMedia, uploads } =
    useProjectDraft()

  const stepIndex = activeInstructionMedia?.stepIndex || 0
  const media = steps[stepIndex]?.media ?? []

  const uploadedMedia = media.filter(m => m?.source === 'upload')
  const [selected, setSelected] = React.useState<UploadPreview[]>([])

  /** ----------------------------
   *  validation rules:
   *  - Up to 10 images OR 1 video.
   *  - No mixing images + video.
   * ---------------------------- */
  const validateFiles = (files: File[]) => {
    const current = steps[stepIndex]?.media ?? []
    const hasVideo = current.some(m => m.type === 'video')
    const imageCount = current.filter(m => m.type === 'image').length

    const incomingVideo = files.some(f => f.type.startsWith('video'))
    const incomingImages = files.filter(f => f.type.startsWith('image')).length

    // File too large (different limits for images vs videos)
    const tooLarge = files.find(f => {
      const isVideo = f.type.startsWith('video')
      const limit = isVideo ? MAX_VIDEO_FILE_SIZE : MAX_IMAGE_FILE_SIZE
      return f.size > limit
    })

    if (tooLarge) {
      const isVideo = tooLarge.type.startsWith('video')
      setFileError(isVideo ? 'Video exceeds 2GB.' : 'Image exceeds 20MB.')
      return false
    }

    // Already have a video → cannot add anything
    if (hasVideo) {
      setFileError('You can add only 1 video OR up to 10 photos.')
      return false
    }

    // Already have images but upload contains a video
    if (incomingVideo && imageCount > 0) {
      setFileError('You cannot add a video when photos already exist.')
      return false
    }

    // Maximum 10 images
    if (incomingImages + imageCount > 10) {
      setFileError('You can upload a maximum of 10 photos.')
      return false
    }

    setFileError(null)
    return true
  }

  const toggleSelect = (item: UploadPreview) => {
    setSelected(prev => {
      const exists = prev.find(s => s.url === item.url)
      if (exists) {
        return prev.filter(s => s.url !== item.url)
      }
      if (prev.length >= 10) {
        return prev
      }
      return [...prev, item]
    })
  }

  const selectionIndex = (url: string) => selected.findIndex(s => s.url === url) + 1 || null

  /** Replace step media with selected items */
  const applySelectedToStep = () => {
    setSteps(prev => {
      const next = [...prev]
      next[stepIndex].media = selected.map(item => ({
        ...item,
        uploading: false,
        source: item.source,
      }))
      next[stepIndex].uploadIds = next[stepIndex].media
        .filter(m => m.id && m.type === 'image')
        .map(m => m.id!)
      next[stepIndex].videoIds = next[stepIndex].media
        .filter(m => m.id && m.type === 'video')
        .map(m => m.id!)
      return next
    })

    setSelected([])
    setActiveInstructionMedia(null)
  }

  // Media updater
  const updateStepMedia = React.useCallback(
    (fn: (prev: UploadPreview[]) => UploadPreview[]) => {
      setSteps(prev => {
        const next = [...prev]
        const updated = fn(prev[stepIndex].media ?? [])
        next[stepIndex] = {
          ...next[stepIndex],
          media: updated,
          uploadIds: updated.filter(m => m.id && m.type === 'image').map(m => m.id!),
          videoIds: updated.filter(m => m.id && m.type === 'video').map(m => m.id!),
        }
        return next
      })
    },
    [setSteps, stepIndex],
  )

  // Upload Handler
  // oxlint-disable-next-line complexity
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList?.length) {
      return
    }

    const files = [...fileList]

    if (!validateFiles(files)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    const currentMedia = steps[stepIndex].media ?? []
    const startIndex = currentMedia.length

    // Placeholder previews
    const placeholders: UploadPreview[] = files.map(f => ({
      url: URL.createObjectURL(f),
      type: f.type.startsWith('video') ? 'video' : 'image',
      uploading: true,
      source: 'upload',
    }))

    updateStepMedia(prev => [...prev, ...placeholders])

    // Actual upload
    const completed: UploadPreview[] = []

    for (const file of files) {
      if (file.type.startsWith('video')) {
        const notif = await uploadVideo(file)
        const upload = notif.social?.feedPayload?.uploadProcessed?.upload
        completed.push({
          id: upload?.id,
          url: upload?.video?.thumbnailUrls?.[0] ?? '',
          type: 'video',
          uploading: false,
          source: 'upload',
        })
      } else {
        let fileToUpload = file
        try {
          // Only image types or .heic extensions reach here
          fileToUpload = await convertImageToWebp(file)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Image conversion failed, using original', error)
        }

        const [notif] = await uploadFeaturedImages([fileToUpload])
        const upload = notif?.social?.feedPayload?.uploadProcessed?.upload
        completed.push({
          id: upload?.id,
          url: upload?.image?.thumbnails?.[0]?.url || upload?.image?.previewUrl || '',
          type: 'image',
          uploading: false,
          source: 'upload',
        })
      }
    }

    // 3. Sync UI state
    updateStepMedia(prev => {
      const out = [...prev]
      completed.forEach((item, i) => {
        out[startIndex + i] = item
      })
      return out
    })

    setSelected(prev => [...prev, ...completed])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Drag & drop upload
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const files = [...e.dataTransfer.files]
    if (!files.length) {
      return
    }

    if (!validateFiles(files)) {
      return
    }

    const transfer = new DataTransfer()
    files.forEach(f => transfer.items.add(f))

    fileInputRef.current!.files = transfer.files
    fileInputRef.current!.dispatchEvent(new Event('change', { bubbles: true }))
  }

  const close = () => applySelectedToStep()

  // Initial load — preselect existing media
  React.useEffect(() => {
    if (isInitialOpen.current) {
      setSelected(steps[stepIndex].media ?? [])
      isInitialOpen.current = false
    }
  }, [activeInstructionMedia, stepIndex, steps])

  return (
    <>
      <div className="flex flex-col pt-3 px-5 gap-2 tablet:p-0 tablet:pt-1 sm:flex-1 sm:min-h-0 pb-20 sm:pb-0">
        <div className="flex items-center sm:gap-4 gap-3 sm:pb-4">
          <Button variant="tertiary" iconLeft={<IconArrowBack />} onClick={close} />
          {!isMobile ? (
            <Typography variant="cf-title-05">
              Add image or video to step {stepIndex + 1}
            </Typography>
          ) : (
            <Typography variant="cf-heading-compact-02">
              Insert images or videos to step {stepIndex + 1}
            </Typography>
          )}
          <span
            className={tw(
              'text-12 font-semibold px-2 py-0.5 rounded-xxl border',
              selected.length === 10
                ? 'bg-cf-grey-100 border-cf-grey-200 text-cf-grey-600'
                : 'bg-cf-theme-green-100 text-cf-theme-green-600 border-cf-theme-green-200',
            )}
          >
            {selected.length}/10
          </span>
        </div>

        {/* uploaded media */}
        {uploadedMedia.length > 0 ? (
          <div className="pb-4 sm:pb-6">
            <div className="flex gap-2 items-center sm:pb-3 pb-2">
              <IconFileUpload className="!size-6 text-cf-grey-800" />
              <Typography
                variant={isMobile ? 'cf-heading-01' : 'cf-heading-03'}
                className="text-cf-grey-800"
              >
                Upload new photo or video
              </Typography>
            </div>

            <div className="grid gap-2 grid-flow-dense grid-cols-[repeat(3,minmax(0,1fr))] sm:grid-cols-[repeat(4,minmax(0,1fr))] lg:grid-cols-[repeat(5,minmax(0,1fr))] overflow-x-auto whitespace-nowrap no-scrollbar">
              {uploadedMedia.map((m, idx) => {
                const selIndex = selectionIndex(m.url)
                return (
                  <div
                    key={idx}
                    className="relative cursor-pointer aspect-square sm:max-w-[228px] w-full mx-auto"
                    onClick={() =>
                      !m.uploading &&
                      toggleSelect({
                        id: m.id,
                        url: m.url,
                        type: m.type,
                        source: m.source ?? 'upload',
                      })
                    }
                  >
                    {/* Image frame */}
                    {m.uploading ? (
                      <div className="absolute inset-0 rounded-xxl bg-cf-grey-50 border border-cf-transparent-grey-1000-12 flex items-center justify-center">
                        <Spinner className="text-cf-purple-900" />
                      </div>
                    ) : (
                      <Image
                        src={m.url}
                        fill
                        alt=""
                        className="rounded-xxl object-cover border border-cf-transparent-grey-1000-12"
                      />
                    )}

                    {/* Select / delete button */}
                    {!m.uploading ? (
                      <button
                        className={tw(
                          'absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center text-12 font-bold shadow-[0px_0px_1px_0px_rgba(19,_19,_21,_0.12),_0px_2px_4px_0px_rgba(19,_19,_21,_0.08)]',
                          selIndex
                            ? 'bg-cf-white text-cf-purple-900'
                            : 'bg-white border-[2px] border-cf-white text-transparent',
                        )}
                        onClick={e => {
                          e.stopPropagation()
                          toggleSelect({
                            id: m.id,
                            url: m.url,
                            type: m.type,
                            source: m.source ?? 'upload',
                          })
                        }}
                      >
                        {selIndex || ''}
                      </button>
                    ) : (
                      <Button
                        size="small"
                        variant="tertiary"
                        className="absolute top-3 right-3"
                        iconLeft={<IconClose className="text-cf-purple-900" />}
                        onClick={e => {
                          e.stopPropagation()
                          updateStepMedia(prev => prev.filter((_, i) => i !== idx))
                        }}
                      />
                    )}
                  </div>
                )
              })}

              {/* Upload tile */}
              {selected.length < 10 && !selected.some(s => s.type === 'video') ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={e => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onClick={triggerUpload}
                  className={tw(
                    'relative flex flex-col justify-center items-center rounded-xxl aspect-square max-w-[228px] w-full mx-auto',
                    'bg-cf-grey-50 border border-dashed border-cf-transparent-grey-1000-12 text-center cursor-pointer hover:bg-cf-grey-100',
                  )}
                >
                  <IconAddPhotoAlternate className="text-cf-purple-900 !size-6 sm:!size-9 mb-4" />
                  <Typography
                    variant="cf-label-02"
                    className="mb-3 text-cf-grey-600 hidden sm:block"
                  >
                    Drag & drop or click.
                  </Typography>
                  <Typography variant="cf-label-02" className="text-cf-grey-600 hidden sm:block">
                    Up to 10 photos or 1 video.
                  </Typography>
                  <Typography
                    variant="cf-label-02"
                    className="text-cf-grey-600 sm:hidden text-center font-medium"
                  >
                    Add more
                  </Typography>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Upload section when no media yet */}
        {uploadedMedia.length === 0 ? (
          <div className="pb-4 sm:pb-0">
            <div className="flex gap-2 items-center sm:pb-3 pb-2">
              <IconFileUpload className="!size-6 text-cf-grey-800" />
              <Typography
                variant={isMobile ? 'cf-heading-01' : 'cf-heading-03'}
                className="text-cf-grey-800"
              >
                Upload new photo or video
              </Typography>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={e => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onClick={triggerUpload}
              className={tw(
                'w-full p-4 rounded-2 border border-dashed border-cf-transparent-grey-1000-12 flex flex-col items-center justify-center cursor-pointer sm:h-56 h-32 hover:bg-cf-grey-100',
                isDragging ? 'bg-cf-grey-100' : 'bg-cf-grey-50',
                selected.length >= 10 || selected.some(s => s.type === 'video')
                  ? 'pointer-events-none opacity-70'
                  : 'hover:bg-cf-grey-100',
              )}
            >
              <IconAddPhotoAlternate className="text-cf-purple-900 sm:!size-9 !size-7 sm:mb-4 mb-2.5" />
              <Typography variant="cf-title-03" className="mb-5 text-cf-grey-1000 hidden sm:block">
                Drag & drop
              </Typography>
              <Button
                variant="tertiary"
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  triggerUpload()
                }}
                className="hidden sm:block mb-4"
                disabled={selected.length >= 10 || selected.some(s => s.type === 'video')}
              >
                Or select
              </Button>

              <Typography variant="cf-label-02" className="text-cf-grey-600 hidden sm:block">
                You can add up to 10 photos or 1 video.
              </Typography>

              <Typography variant="cf-label-02" className="text-cf-grey-600 sm:hidden text-center">
                Click here to browse. <br />
                <b>Up to 10 photos or 1 video.</b>
              </Typography>
            </div>
          </div>
        ) : null}

        {/* Image library */}
        {uploads.length !== 0 ? (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex gap-2 items-center sm:pb-3">
              <IconPhotoPrints className="!size-6 text-cf-grey-800" />
              <Typography
                variant={isMobile ? 'cf-heading-01' : 'cf-heading-03'}
                className="text-cf-grey-800"
              >
                Choose from previous uploads
              </Typography>
            </div>

            <div className="grid gap-2 grid-flow-dense grid-cols-[repeat(3,minmax(0,1fr))] sm:grid-cols-[repeat(4,minmax(0,1fr))] lg:grid-cols-[repeat(5,minmax(0,1fr))] overflow-x-auto whitespace-nowrap no-scrollbar">
              {uploads.map((file, i) => {
                const item: UploadPreview = {
                  id: file.id,
                  url: file.url,
                  type: file.type,
                  uploading: file.uploading,
                  source: 'library',
                }

                const selIndex = selectionIndex(item.url)

                const disabled =
                  (selected.some(s => s.type === 'video') && item.type === 'image') ||
                  (selected.some(s => s.type === 'image') && item.type === 'video')

                return (
                  <div
                    key={file.id || i}
                    className={tw(
                      'relative cursor-pointer aspect-square w-full mx-auto',
                      disabled && 'opacity-40 pointer-events-none',
                    )}
                    onClick={() => !item.uploading && toggleSelect(item)}
                  >
                    {item.uploading ? (
                      <div className="absolute inset-0 rounded-xxl bg-cf-grey-50 border border-cf-transparent-grey-1000-12 flex items-center justify-center">
                        <Spinner className="text-cf-purple-900" />
                      </div>
                    ) : (
                      <Image
                        src={item.url}
                        alt=""
                        fill
                        className="rounded-xxl object-cover border border-cf-transparent-grey-1000-12"
                      />
                    )}

                    {/* Select indicator */}
                    {!item.uploading ? (
                      <div
                        className={tw(
                          'absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center text-12 font-bold shadow-[0px_0px_1px_0px_rgba(19,_19,_21,_0.12),_0px_2px_4px_0px_rgba(19,_19,_21,_0.08)]',
                          selIndex
                            ? 'text-cf-purple-900 bg-cf-white'
                            : 'bg-white border-[2px] border-cf-white text-transparent',
                        )}
                      >
                        {selIndex || ''}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,.mp4,.mov,.webm,video/mp4,video/quicktime,video/webm"
          onChange={handleFileUpload}
        />
      </div>
      <div
        className={tw(
          'sm:relative fixed bottom-0 left-0 right-0 bg-cf-white z-[999] sm:pb-4 table:pb-0',
          'flex sm:justify-end pt-4 border-t border-cf-transparent-grey-1000-12 px-5 pb-5 sm:border-none sm:px-0 sm:pb-0',
        )}
      >
        <Button
          disabled={selected.length === 0}
          onClick={applySelectedToStep}
          className="sm:w-auto w-full"
        >
          Add to step
        </Button>
      </div>
    </>
  )
}
